import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';

import { selectSubscriptions } from 'store/subscriptions/selectors';
import { fetchDiscountSubscriptions, resetSubscription } from 'store/subscriptions/actions';

import { DISCOUNT_TYPES } from 'constants/subscriptions';
import { SUBSCRIPTION_ACTIVE } from 'constants/payments';

import {
    OfferTypes,
    cancelFlow_offerResultScreenLoad,
    cancelFlow_offerResultClick,
} from 'services/analytics/trackers/cancelFlow';

import { useSelector } from 'react-redux';
import { useModals } from 'hooks/modals';

import { getPriceFromCents } from 'helpers/prices';

import { ModalName } from 'types/modals';
import { isMainSubscription } from 'pages/AccountSettings/components/SubscriptionsTab/components/SubscriptionCard/helpers';

const useReturnOffer = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal, closeModal } = useModals();

    const { discountSubscription, subscriptions } = useSelector(selectSubscriptions);

    const resetCandidate = subscriptions.find(
        ({ state, product_type, discounts, cancelled_at, internal_cancel_at, is_upgrade }) => {
            return (
                !discounts.discount &&
                state === SUBSCRIPTION_ACTIVE &&
                (cancelled_at || internal_cancel_at) &&
                isMainSubscription(product_type)
            );
        }
    );

    const withFee = !!resetCandidate?.fee?.fee_expired_at;

    useEffect(() => {
        if (!resetCandidate || discountSubscription || withFee) return;

        dispatch(
            fetchDiscountSubscriptions({
                discount_type: DISCOUNT_TYPES.extra,
                external_id: resetCandidate.external_id,
                provider: resetCandidate.payment_provider,
            })
        );
    }, [discountSubscription, resetCandidate]);

    const hasOfferFetched = resetCandidate && discountSubscription;

    const resetCandidatePriceData = resetCandidate
        ? {
              currency: resetCandidate.product.currency,
              period: resetCandidate.subscription_period,
              price: resetCandidate.product.price,
          }
        : {
              currency: '',
              period: 0,
              price: '',
          };

    const discountPriceData = discountSubscription
        ? {
              currency: discountSubscription!.currency,
              period: discountSubscription!.subscription_period,
              price: getPriceFromCents(discountSubscription!.amount) + '',
          }
        : null;

    const onResetSubscription = useCallback(() => {
        if (resetCandidate && discountSubscription) {
            openModal(ModalName.WAITING_MODAL, { title: 'subscription.modals.updating' });

            dispatch(
                resetSubscription({
                    id: resetCandidate.external_id,
                    product: discountSubscription,
                    discount_type: DISCOUNT_TYPES.extra,
                    provider: resetCandidate.payment_provider,
                    onSuccess: () => {
                        closeModal();
                    },
                    onError: () => {
                        openModal(ModalName.WARNING_MODAL, {
                            offerType: OfferTypes.Return_offer,
                            onMainBtnClick: () => {
                                cancelFlow_offerResultClick({
                                    action: 'try_again',
                                    result: 'fail',
                                    subscription_id: resetCandidate.external_id,
                                    offer_type: OfferTypes.Return_offer,
                                });
                                onResetSubscription();
                            },
                            onSupportClick: () =>
                                cancelFlow_offerResultClick({
                                    action: 'contact_support',
                                    result: 'fail',
                                    subscription_id: resetCandidate.external_id,
                                    offer_type: OfferTypes.Return_offer,
                                }),
                            title: t('subscription.cancelFlow.errorModal.title'),
                            analyticScreenLoadHandler() {
                                cancelFlow_offerResultScreenLoad({
                                    result: 'fail',
                                    offer_type: OfferTypes.Return_offer,
                                    subscription_id: resetCandidate?.external_id,
                                });
                            },
                        });
                    },
                })
            );
        }
    }, [resetCandidate, discountSubscription]);

    return {
        withFee,
        resetCandidate,
        hasOfferFetched,
        discountSubscription,
        discountPriceData,
        resetCandidatePriceData,
        onResetSubscription,
    };
};

export default useReturnOffer;
